import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoGraph as AiUsageIcon,
  Refresh as RefreshIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAiUsage } from '../../contexts/AiUsageContext';
import { useTenant } from '../../contexts/TenantContext';
import { AiCostGrid, TokenUsageBar } from '../common';
import {
  USAGE_SEVERITY_COLORS,
  USAGE_SEVERITY_CRITICAL,
  USAGE_SEVERITY_OK,
  formatCost,
  getSummarySeverity,
} from '../../constants/aiUsage';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';

/**
 * "AI Usage" control in the header.
 *
 * Renders nothing until JLL has given the client a token allowance — a client
 * with no budget has no meter to show. The icon carries a severity dot so users
 * notice they are approaching the limit without opening the panel.
 */
const AiUsageMenu = () => {
  const { summary, byModel, loading, error, isConfigured, canViewCost, refresh } = useAiUsage();
  const { currentTenant, isJLLUser, hasRole } = useTenant();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const canManageClients = isJLLUser() && hasRole('JLL Super Admin');

  // No allowance configured: stay out of the header entirely.
  if (!isConfigured || !summary) {
    return null;
  }

  const severity = getSummarySeverity(summary);
  const severityColor = USAGE_SEVERITY_COLORS[severity];
  const isBreached = severity === USAGE_SEVERITY_CRITICAL;
  const currency = summary.currency || 'USD';

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleManageClients = () => {
    handleClose();
    navigate('/clients');
  };

  return (
    <>
      <Tooltip title={`AI usage — ${severityColor.label}`} arrow>
        <IconButton
          onClick={handleOpen}
          aria-label="AI token usage"
          sx={{
            color: '#000000',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <Badge
            variant="dot"
            invisible={severity === USAGE_SEVERITY_OK}
            sx={{
              '& .MuiBadge-dot': {
                backgroundColor: severityColor.main,
                boxShadow: '0 0 0 2px #FFFFFF',
              },
            }}
          >
            <AiUsageIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            // Wide enough for the cost grid on desktop, never wider than a phone
            width: { xs: 'calc(100vw - 2rem)', sm: 460, md: 620 },
            maxWidth: 'calc(100vw - 2rem)',
            maxHeight: 'calc(100vh - 6rem)',
            overflowY: 'auto',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            fontFamily: FONT_FAMILY_PRIMARY,
          },
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                AI Token Usage
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.tenantName || currentTenant?.tenantName || 'Current client'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={severityColor.label}
                size="small"
                sx={{
                  backgroundColor: severityColor.soft,
                  color: severityColor.main,
                  fontWeight: 600,
                }}
              />
              <Tooltip title="Refresh" arrow>
                <IconButton size="small" onClick={refresh} disabled={loading} aria-label="Refresh AI usage">
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {isBreached && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'flex-start',
                p: 1.5,
                mb: 2,
                borderRadius: '6px',
                backgroundColor: severityColor.soft,
              }}
            >
              <WarningIcon sx={{ color: severityColor.main, fontSize: '1.1rem', mt: '2px' }} />
              <Typography variant="caption" sx={{ color: severityColor.main }}>
                {summary.isEnforced
                  ? 'The AI token limit has been reached. New AI requests are blocked until JLL raises the limit.'
                  : 'The AI token limit has been exceeded. Further usage is charged at each model’s rate.'}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TokenUsageBar
              label="Input tokens"
              used={summary.inputTokensUsed}
              limit={summary.inputTokenLimit}
              percent={summary.inputUsagePercent}
              alertThresholdPercent={summary.alertThresholdPercent}
              compact
            />
            <TokenUsageBar
              label="Output tokens"
              used={summary.outputTokensUsed}
              limit={summary.outputTokenLimit}
              percent={summary.outputUsagePercent}
              alertThresholdPercent={summary.alertThresholdPercent}
              compact
            />
          </Box>

          {/* Cost is a commercial matter between JLL and the client's administrator.
              The server strips the figures for everyone else and tells us via
              canViewCost, so this is not the only thing standing between a
              viewer and the numbers. */}
          {canViewCost && (
            <>
              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Cost by model
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {formatCost(summary.totalCost, currency)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Charged only on tokens beyond the allowance, at the rate of the model that ran.
              </Typography>

              <AiCostGrid
                rows={byModel}
                currency={currency}
                compact
                emptyMessage="No AI cards have been run for this client yet."
              />
            </>
          )}

          {error && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              Showing the last known figures — {error}
            </Typography>
          )}

          {canManageClients && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleManageClients} sx={{ textTransform: 'none' }}>
                Manage client limits
              </Button>
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default AiUsageMenu;
